import React from 'react';
import classNames from 'classnames';

import Field from '../../Field';

import css from './SectionContainer.module.css';
import innerPageCss from './InnerPage.module.css';
import ScrollDown from '../../../../components/CustomBuild/InteractiveCursor/ScrollDown';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This component can be used to wrap some common styles and features of Section-level components.
 * E.g: const SectionHero = props => (<SectionContainer><H1>Hello World!</H1></SectionContainer>);
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id id of the section
 * @param {string?} props.as tag/element name. Defaults to 'section'.
 * @param {ReactNode} props.children
 * @param {Object} props.appearance
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} containing wrapper that can be used inside Block components.
 */
const SectionContainer = props => {
  const { className, rootClassName, id, as, children, appearance, options, ...otherProps } = props;
  const Tag = as || 'section';
  const isMediaInnerPage = id === 'media-hero-section';
  const classes = classNames(rootClassName || css.root, className, {
    [innerPageCss.mediaPage]: isMediaInnerPage,
  });

  const heroVideoRefCallback = node => {
    if (!node) return;
    node.play().catch(() => {
      // Autoplay blocked — silently ignore.
    });
  };

  return (
    <Tag className={classes} id={id} {...otherProps}>
      {appearance?.fieldType === 'customAppearance' ? (
        <Field
          data={{ alt: `Background image for ${id}`, ...appearance }}
          className={className}
          options={options}
        />
      ) : null}

      <div className={css.sectionContent}>{children}</div>
      {id === 'home-hero' ? <ScrollDown nextSectionId={'hero-info'} /> : null}
      {id === 'home-hero' ? (
        <div className={css.videoContainer}>
          {/* Mobile: ≤600px | Tablet: 601px–1024px | Desktop: >1024px */}
          <video
            ref={heroVideoRefCallback}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className={css.heroVideo}
          >
            <source
              src="/static/video/hook_video/hook_FINAL_CUT_9_16.webm"
              media="(max-width: 600px)"
              type="video/webm"
            />
            <source
              src="/static/video/hook_video/hook_FINAL_CUT_3_4.webm"
              media="(max-width: 1240px)"
              type="video/webm"
            />
            <source
              src="/static/video/hook_video/hook_FINAL_CUT_UHD.webm"
              type="video/webm"
            />
          </video>
        </div>
      ) : null}
    </Tag>
  );
};

export default SectionContainer;
